import { AsyncPipe, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ApplianceCategoryRadioListComponent,
  ApplianceSort,
  BrandCategoryApplianceRadioListComponent,
  BrandFilterListComponent,
  BrandSort,
  ModelNumberSelectTypeaheadComponent,
} from '@domgen/dgx-fe-catalogue';
import { ClientBrandCode, WINDOW } from '@domgen/dgx-fe-common';
import { CardComponent } from '@domgen/dgx-fe-components/card';
import { RichTextComponent } from '@domgen/dgx-fe-components/content';
import { TestHookDirective } from '@domgen/dgx-fe-components/general';
import { IconComponent } from '@domgen/dgx-fe-components/icon';
import {
  SectionLoadingComponent,
  SectionLoadingDirective,
} from '@domgen/dgx-fe-components/loading';
import { PictureComponent } from '@domgen/dgx-fe-components/media';
import { PanelComponent } from '@domgen/dgx-fe-components/panel';
import { ProgressBarComponent } from '@domgen/dgx-fe-components/progress';
import { OverrideAddressComponent } from '@domgen/dgx-fe-forms/address';
import { CheckboxComponent } from '@domgen/dgx-fe-forms/checkbox';
import { CalendarInputComponent } from '@domgen/dgx-fe-forms/date';
import {
  CurrencyInputComponent,
  EmailInputComponent,
  TelephoneInputComponent,
  TextInputComponent,
} from '@domgen/dgx-fe-forms/input';
import {
  FormFieldComponent,
  FormFieldDirective,
  FormFieldsetComponent,
  FormLabelComponent,
  FormLegendComponent,
  FormRowComponent,
  FormSubmitButtonComponent,
} from '@domgen/dgx-fe-forms/layout';
import {
  DependantControlDirective,
  FormDisabledDirective,
  FormSubmitDirective,
} from '@domgen/dgx-fe-forms/shared';
import {
  SteppedFormComponent,
  SteppedFormFieldComponent,
} from '@domgen/dgx-fe-forms/stepped';
import {
  GAClickDirective,
  GAContainerDirective,
  GAEvent,
  GAFormFieldDirective,
  GAService,
} from '@domgen/dgx-fe-ga';
import {
  provideTranslationDictionaryMapping,
  TranslatePipe,
} from '@domgen/dgx-fe-i18n';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';
import {
  ApplianceDetailsSteppedFormValue,
  RegistrationCodeFormValue,
  RegistrationRequest,
} from '@us-registration/util-models';
import { posthog } from 'posthog-js';
import { translation } from '../../i18n/translation.constant';
import { ApplianceDetailsSteppedTestHooks } from '../../models/test/appliance-details-stepped-test-hooks.enum';
import { ApplianceDetailsSteppedFormBuilder } from '../../services/appliance-details-stepped-form-builder/appliance-details-stepped-form-builder.service';
import { ApplianceDetailsSteppedFormComponentService } from '../../services/appliance-details-stepped-form-component/appliance-details-stepped-form-component.service';
import { SerializationService } from '../../services/serialization/serialization.service';
import { ApplianceDetailsSteppedContentToggleComponent } from '../appliance-details-stepped-content-toggle/appliance-details-stepped-content-toggle.component';
import { ApplianceDetailsSteppedFormFindModelSerialPanelComponent } from '../appliance-details-stepped-form-find-model-serial-panel/appliance-details-stepped-form-find-model-serial-panel.component';
import { ApplianceDetailsSteppedFormRegistrationCodeConfirmationPanelComponent } from '../appliance-details-stepped-form-registration-code-confirmation-panel/appliance-details-stepped-form-registration-code-confirmation-panel.component';

@Component({
  selector: 'us-registration-appliance-details-stepped-form',
  templateUrl: './appliance-details-stepped-form.component.html',
  styleUrls: ['./appliance-details-stepped-form.component.scss'],
  providers: [
    ApplianceDetailsSteppedFormBuilder,
    provideTranslationDictionaryMapping(translation),
    SerializationService,
    ApplianceDetailsSteppedFormComponentService,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ApplianceCategoryRadioListComponent,
    ApplianceDetailsSteppedContentToggleComponent,
    ApplianceDetailsSteppedContentToggleComponent,
    ApplianceDetailsSteppedFormFindModelSerialPanelComponent,
    ApplianceDetailsSteppedFormFindModelSerialPanelComponent,
    ApplianceDetailsSteppedFormRegistrationCodeConfirmationPanelComponent,
    AsyncPipe,
    BrandCategoryApplianceRadioListComponent,
    BrandFilterListComponent,
    CalendarInputComponent,
    CardComponent,
    CheckboxComponent,
    CurrencyInputComponent,
    DependantControlDirective,
    EmailInputComponent,
    FormDisabledDirective,
    FormFieldComponent,
    FormFieldDirective,
    FormFieldsetComponent,
    FormLabelComponent,
    FormLegendComponent,
    FormRowComponent,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    GAClickDirective,
    GAContainerDirective,
    GAFormFieldDirective,
    IconComponent,
    ModelNumberSelectTypeaheadComponent,
    NgIf,
    OverrideAddressComponent,
    PanelComponent,
    PictureComponent,
    ProgressBarComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    RichTextComponent,
    SectionLoadingComponent,
    SectionLoadingDirective,
    SteppedFormComponent,
    SteppedFormFieldComponent,
    TelephoneInputComponent,
    TestHookDirective,
    TextInputComponent,
    TranslatePipe,
  ],
})
export class ApplianceDetailsSteppedFormComponent
  implements OnInit, AfterViewInit
{
  @Input() set clientBrandCode(clientBrandCode: ClientBrandCode | undefined) {
    this.cs.updateClientBrandCode(clientBrandCode);
  }

  @Output() activeStep = new EventEmitter<number>();
  @Output() submitted = new EventEmitter<RegistrationRequest>();
  @ViewChild(SteppedFormComponent) steppedFormComponent?: SteppedFormComponent;

  applianceSort = ApplianceSort;
  brandSort = BrandSort;
  hasModelSerialNumber = true;
  registrationHasModelAndSerialNumber = true;
  testHooks = ApplianceDetailsSteppedTestHooks;

  constructor(
    @Inject(WINDOW) private readonly window: Window,
    readonly cs: ApplianceDetailsSteppedFormComponentService,
    private readonly destroyRef: DestroyRef,
    private readonly gaService: GAService
  ) {}

  ngOnInit(): void {
    this.cs.buildForm();
    this.cs.buildRegistrationCodeForm();
    this.cs.listenToRouteFragmentChanges();
  }

  ngAfterViewInit(): void {
    this.cs.updateSteppedFormComponent(this.steppedFormComponent);
  }

  toggleHasRegistrationCode(hasRegistrationCode: boolean): void {
    this.cs.updateHasRegistrationCode(hasRegistrationCode);
  }

  toggleHasModelSerialNumber(): void {
    this.hasModelSerialNumber = !this.hasModelSerialNumber;
  }

  onFormSubmit(formValue: ApplianceDetailsSteppedFormValue): void {
    this.cs
      .onFormSubmit(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((registrationRequest: RegistrationRequest) => {
        this.submitted.emit(registrationRequest);
        this.triggerFetchingQuoteLoadingOverlay();

        // PostHog capture for appliance category selection e.g. "frdg-fridge-selected"
        // Post-WIB capture for appliance name selection will change to a code e.g."frdg-frdg-selected"
        if (
          formValue.applianceCategoryCode &&
          registrationRequest.merchandise
        ) {
          const appliance = registrationRequest.merchandise[0].type
            .replace(' ', '-')
            .toLowerCase();
          const applianceCategoryCode =
            formValue.applianceCategoryCode.toLowerCase();

          posthog.capture(`${applianceCategoryCode}-${appliance}-selected`);
        }

        this.gaService.tag({
          pagename: 'appliance-enter-personal-details',
          eventLabel: 'Enter your details',
          eventAction: 'Continue',
          eventCategory: 'Continue button',
          event: GAEvent.GenericEvent,
        });
      });
  }

  private triggerFetchingQuoteLoadingOverlay(): void {
    this.cs.startFetchingQuoteProgress();
    this.cs.updateDisplayFetchingQuoteLoadingOverlay(true);
    this.window.scrollTo({ top: 0, behavior: 'smooth' });

    setTimeout(() => {
      //required for IOS
      document.scrollingElement?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }

  onRegistrationCodeFormSubmit(formValue: RegistrationCodeFormValue): void {
    this.cs
      .onRegistrationCodeSubmit(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cs.updateHasRegistrationCode(false);
        this.cs.updateNavigatedToPriceStep(false);
      });
  }
}
